@if (isOpen) {
  <div class="fixed inset-0 bg-black/60 flex justify-center items-center z-[60]"
    (click)="close.emit()">
    <div
      class="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-6 rounded-lg shadow-2xl w-full max-w-6xl max-h-[85vh] flex flex-col"
      (click)="$event.stopPropagation()">

      <!-- Header -->
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-xl font-bold">Edit Source Chunk(s)</h3>
        <button
          (click)="close.emit()"
          class="p-1 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </button>
      </div>

      <!-- Suggestion Context Banner -->
      @if (isSuggestionFlow) {
        <div class="bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-900 text-gray-800 dark:text-yellow-100 p-3 rounded-md text-sm mb-4">
          <p>
            Your suggestion of "<strong>{{ suggestionContext?.desiredChange }}</strong>" on
            <strong>{{ displayNodeLabel }}:</strong>
            "{{ suggestionContext?.oldValue }}" requires updating the Chunks.

            @if (isLoadingSuggestions) {
              <br>
              <span class="mt-1 block">
                We are analyzing the impact and will suggest the necessary change. Please wait.
              </span>
            }

            @if (!isLoadingSuggestions && !isUpdatingChunks) {
              <br>
              <span class="mt-1 block">
                Please review and confirm the following changes to the Chunks.
              </span>
            }
          </p>
        </div>
      }

      <!-- API Error Banner -->
      @if (apiError) {
        <div class="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 p-3 rounded-md text-sm mb-4">
          <p class="font-semibold text-red-700 dark:text-red-300">Analysis Failed</p>
          <p class="text-red-600 dark:text-red-400">{{ apiError }}</p>
          <button (click)="clearApiError.emit()" class="text-xs text-red-700 dark:text-red-300 underline mt-1">
            Dismiss
          </button>
        </div>
      }

      <!-- Content States -->

      <!-- State 1: Analyzing -->
      @if (isLoadingSuggestions) {
        <div class="flex flex-col items-center justify-center h-full min-h-[40vh]">
          <svg class="w-10 h-10 text-blue-600 dark:text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-lg font-semibold text-gray-700 dark:text-gray-200 mt-4">We are analyzing your change</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">Please wait while we suggest the necessary changes.</p>
        </div>
      }

      <!-- State 2: Updating -->
      @if (isUpdatingChunks && !isLoadingSuggestions) {
        <div class="flex flex-col items-center justify-center h-full min-h-[40vh]">
          <svg class="w-10 h-10 text-blue-600 dark:text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-lg font-semibold text-gray-700 dark:text-gray-200 mt-4">Updating Knowledge Graph</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">Your changes are being processed.</p>
        </div>
      }

      <!-- State 3: Editor -->
      @if (!isLoadingSuggestions && !isUpdatingChunks) {
        @if (hasChunks) {
          <div class="flex-1 overflow-y-auto space-y-2 border border-gray-200 dark:border-gray-700 rounded-md">
            @for (chunk of chunks; track chunk.chunkId; let i = $index) {
              <div class="border-b border-gray-200 dark:border-gray-700 last:border-b-0 mb-0">
                <!-- Accordion Header -->
                <button
                  (click)="toggleAccordion(chunk.chunkId)"
                  class="w-full flex items-center justify-between p-3 text-left bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700">
                  <span class="text-sm font-medium text-gray-700 dark:text-gray-200">Chunk #{{ i + 1 }}</span>
                  <svg class="w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform"
                       [class.rotate-180]="openAccordionId === chunk.chunkId"
                       fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>

                <!-- Accordion Body -->
                @if (openAccordionId === chunk.chunkId) {
                  <div class="p-4 bg-white dark:bg-gray-900 flex flex-col lg:flex-row gap-4 h-[50vh] flex-grow">
                    <!-- Column 1: Current -->
                    <div class="flex-1 lg:w-1/3 flex flex-col">
                      <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 flex-shrink-0">Current</label>
                      <div class="w-full p-2 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-md text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line overflow-y-auto h-full">
                        {{ chunk.originalValue || "(Empty)" }}
                      </div>
                    </div>

                    <!-- Column 2: Editable -->
                    <div class="flex-1 lg:w-1/3 flex flex-col">
                      <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 flex-shrink-0">
                        {{ aiSuggested ? 'AI Suggested Change' : 'Your Suggested Change' }}
                      </label>
                      <textarea
                        [ngModel]="editedChunks[chunk.chunkId]"
                        (ngModelChange)="handleTextChange(chunk.chunkId, $event)"
                        class="w-full p-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-blue-400 focus:outline-none text-sm resize-none flex-grow h-full"
                        maxlength="500">
                      </textarea>
                    </div>

                    <!-- Column 3: Preview -->
                    <div class="flex-1 lg:w-1/3 flex flex-col">
                      <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 flex-shrink-0">Change Preview</label>
                      <div class="w-full p-2 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-md text-sm whitespace-pre-line overflow-y-auto h-full">
                        @for (part of diffResults[chunk.chunkId]; track $index) {
                          <span [ngClass]="{
                              'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 font-medium': part.added,
                              'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 line-through': part.removed,
                              'text-gray-700 dark:text-gray-300': !part.added && !part.removed
                            }">
                            {{ part.value }}
                          </span>
                        }
                      </div>
                      <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 flex-shrink-0">
                        <span class="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 px-1 rounded-sm">Highlight</span> = Added,
                        <span class="bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 line-through px-1 rounded-sm">Strikethrough</span> = Removed
                      </p>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        } @else {
          <div class="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400 italic min-h-[40vh]">
            {{ !apiError ? "No editable chunks were found for this suggestion." : "Analysis failed to find chunks." }}
          </div>
        }

        <!-- Footer Buttons -->
        <div class="mt-6 flex justify-end gap-3 border-t pt-4 border-gray-200 dark:border-gray-700">
          <button
            (click)="close.emit()"
            class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">
            Cancel
          </button>
          <button
            (click)="handleSaveChanges()"
            [disabled]="isUpdatingChunks || !hasChunks || !!apiError"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 dark:disabled:bg-blue-900/50 disabled:cursor-not-allowed">
            {{ isUpdatingChunks ? 'Confirming...' : 'Confirm Changes' }}
          </button>
        </div>
      }
    </div>
  </div>
}
